import React, { useState, useMemo, useCallback } from 'react';
import CardDetail from '../CardDetail.js';
import CostCurveChart from '../CostCurveChart.js';
import { getCardName, getCardTypeName, getUILoc } from '../../utils/cardUtils.js';
import { CardIllustration } from '../Card.js';
import audioManager from '../../utils/AudioManager.js';
import '../CostCurveChart.css';
import './HypothesisDeckEditorView.css';

const HypothesisDeckPreviewView = ({ deckCards, cardDefs, title, onBack }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [costCurveMode, setCostCurveMode] = useState('single');

  // カードごとの枚数集約
  const cardCountMap = useMemo(() => {
    return deckCards.reduce((acc, cardId) => {
      acc[cardId] = (acc[cardId] || 0) + 1;
      return acc;
    }, {});
  }, [deckCards]);

  const previewCostCurveData = useMemo(() => {
    const counts = {};
    deckCards.forEach(cardId => {
      const cardDef = cardDefs[cardId] || Object.values(cardDefs).find(def => def.name === cardId);
      if (cardDef) {
        const cost = cardDef.required_scale;
        counts[cost] = (counts[cost] || 0) + 1;
      }
    });

    if (costCurveMode === 'single') {
      const maxCost = Math.max(...Object.keys(counts).map(Number), 0);
      const data = [];
      for (let i = 0; i <= maxCost; i++) {
        if (counts[i] > 0) {
          data.push({ label: `${i}`, count: counts[i] });
        }
      }
      return data;
    } else {
      const groups = { '0-4': 0, '5-9': 0, '10-14': 0, '15-19': 0, '20+': 0 };
      Object.entries(counts).forEach(([cost, count]) => {
        const c = parseInt(cost, 10);
        if (c <= 4) groups['0-4'] += count;
        else if (c <= 9) groups['5-9'] += count;
        else if (c <= 14) groups['10-14'] += count;
        else if (c <= 19) groups['15-19'] += count;
        else groups['20+'] += count;
      });
      return Object.entries(groups).map(([label, count]) => ({ label, count }));
    }
  }, [deckCards, cardDefs, costCurveMode]);

  const handleCardClick = useCallback((cardId) => {
    audioManager.playSE('select');
    const cardDef = cardDefs[cardId] || Object.values(cardDefs).find(def => def.name === cardId);
    if (cardDef) {
      setSelectedCard(cardDef);
    }
  }, [cardDefs]);

  const toggleCostCurveMode = () => {
    audioManager.playSE('select');
    setCostCurveMode(prev => prev === 'single' ? 'grouped' : 'single');
  };

  return (
    <div className="deck-selection-screen hypothesis-deck-editor-view" style={{ zIndex: 100, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      {selectedCard && (
        <CardDetail card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
      <div className="screen-header">
        <div className="header-left">
          <h1 className="hypothesis-editor-title">{title || getUILoc('BUTTONS.CHECK_DECK')}</h1>
        </div>
        <button 
          className="back-button" 
          onClick={() => {
            audioManager.playSE('cancel');
            onBack();
          }}
        >
          ← {getUILoc('BUTTONS.BACK')}
        </button>
      </div>

      <div className="deck-preview" style={{ margin: '20px auto', maxWidth: '900px' }}>
        <h3>{title}</h3>
        <div className="deck-cards">
          <h4>{getUILoc('LABELS.DECK_CONTENT')} ({getUILoc('UNITS.CARDS', { count: deckCards.length })})</h4>
          {deckCards.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', padding: '30px' }}>
              {getUILoc('MESSAGES.EMPTY_DECK_ERROR')}
            </p>
          ) : (
            <div className="card-list">
              {Object.entries(cardCountMap).map(([cardId, count]) => {
                const cardDef = cardDefs[cardId] || Object.values(cardDefs).find(def => def.name === cardId);
                if (!cardDef) return null;

                return (
                  <div 
                    key={cardId} 
                    className="deck-card-item"
                    onClick={() => handleCardClick(cardId)}
                  >
                    <CardIllustration cardId={cardDef.id} isMini={true} />
                    <span className="card-count">×{count}</span>
                    <span className="card-name">{getCardName(cardDef.id)}</span>
                    <span className="card-type">({getCardTypeName(cardDef.card_type)})</span>
                    <span className="card-scale">{getUILoc('LABELS.REQUIRED_SCALE')}:{cardDef.required_scale}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cost-curve-wrapper">
          <div className="cost-curve-header">
            <h3>{getUILoc('LABELS.SCALE_DISTRIBUTION')}</h3>
            <button onClick={toggleCostCurveMode}>
              {getUILoc('LABELS.SWITCH_DISPLAY')} ({costCurveMode === 'single' ? getUILoc('LABELS.STEP_1') : getUILoc('LABELS.STEP_5')})
            </button>
          </div>
          <CostCurveChart data={previewCostCurveData} />
        </div>

        <div className="deck-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
          <button 
            className="back-button" 
            onClick={() => {
              audioManager.playSE('cancel');
              onBack();
            }}
          >
            {getUILoc('BUTTONS.BACK')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HypothesisDeckPreviewView;
